import { Box, Typography } from '@mui/material';
import { useListPhasesQuery } from '../../api/content.api';
import { LoadingSkeleton } from '../../components/LoadingSkeleton';
import { PhaseCardCrossCutting } from '../../components/PhaseCardCrossCutting';
import { StageColumn } from '../../components/StageColumn';
import { splitCrossCutting } from '../../content';
import { cardGridSx, sectionSpacingSx, tokens } from '../../theme';
import { ContentState } from '../../components/ContentState';

/**
 * `/sdlc` — the catalog's front page: the whole lifecycle as four stage columns
 * plus the cross-cutting phase beneath them.
 *
 * **The grid's columns are stages, and each column divides itself into the same
 * number of card rows.** That is what makes a card in row two of Design land
 * level with the one in row two of Build, however many lines their titles take.
 * `StageColumn` owns the mechanics and why a flex stack could not do it.
 *
 * **At tablet the grid is three columns, not two**, so the fourth stage wraps
 * under the first. Halving would put two stages side by side and read as a
 * pairing the taxonomy does not have.
 */
/** How many stage columns tablet gets, and so which stages wrap onto their own row. */
const TABLET_COLUMNS = tokens.gridColumns.stage.tablet;

export const LifecyclePage = () => {
  const { data: phases, isLoading, error, refetch } = useListPhasesQuery();
  const { stages, crossCutting } = splitCrossCutting(phases ?? []);

  // Every column is divided into this many card rows, so a phase in row two of
  // Design lands level with the phase in row two of Build. Derived from the
  // fullest stage rather than fixed at three: a stage gaining a phase should
  // reflow the grid rather than overflow its column.
  const rowCount = Math.max(1, ...stages.map((group) => group.phases.length));

  return (
    <Box sx={{ paddingBlock: 8 }}>
      <Box>
        <ContentState
          isLoading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          errorTitle="Unable to load the lifecycle"
          errorNoun="lifecycle"
          isEmpty={phases !== undefined && phases.length === 0}
          emptyTitle="No phases yet"
          emptyMessage="The lifecycle has no phases catalogued. They will appear here as the toolkit grows."
          skeleton={<LoadingSkeleton layout="lifecycle" label="Loading the lifecycle" />}
        >
          {/*
            Inside the state rather than above it, so the skeleton stands in for
            the heading too. The words are static copy and could be painted
            immediately — but the designs skeletonise them, and a real 32px
            heading over a grid of grey bars reads as a page that half failed
            rather than as one that is arriving.
          */}
          <Typography variant="h1" component="h1">
            The AI-Augmented SDLC
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', marginTop: 3 }}>
            Eleven phases across four stages. Each phase shows what AI changes in it, and how
            much of the method library sits underneath.
          </Typography>

          <Box component="section" sx={{ ...cardGridSx('stage'), ...sectionSpacingSx }}>
            {stages.map((group, index) => (
              <StageColumn
                key={group.stage}
                group={group}
                rowCount={rowCount}
                // Tablet lays the stages out three to a row, so a fourth wraps
                // alone. Any stage in that trailing remainder takes the full
                // width rather than stranding itself in one of three columns.
                fullWidthAtTablet={index >= stages.length - (stages.length % TABLET_COLUMNS)}
              />
            ))}
          </Box>

          {crossCutting.map((phase) => (
            <Box key={phase.id} sx={sectionSpacingSx}>
              <PhaseCardCrossCutting phase={phase} />
            </Box>
          ))}
        </ContentState>
      </Box>
    </Box>
  );
};
